import React, { useCallback, useEffect, useRef, useState } from "react";
import ChatWidgetLauncher from "./ChatWidgetLauncher";
import ChatWidget from "./ChatWidget";
import useChatWidgetState from "../hooks/useChatWidgetState";
import {
  captureLead, getVisitorConversation, getWidgetBot, sendVisitorMessage, startConversation
} from "../../api/services/widgetService";
import { APPEARANCE_EVENT, withDefaults } from "../../appearanceBranding/appearanceStore";
import { lengthError, phoneError } from "../../utils/validation";
import "./ChatWidget.css";

const DEFAULT_LEAD_FORM = { phone: "optional", company: false, message: false };

const DEFAULT_QUICK_ACTIONS = [
  { id: "pricing", label: "Pricing Plans" },
  { id: "demo", label: "Book a Demo" },
  { id: "support", label: "Technical Support" },
];

const SESSION_KEY = "chatbizz_widget_session";   // { [botId]: conversationId } — survives refreshes
const CONTEXT_KEY = "chatbizz_active_session";   // display copy of the session, shared with the task panels
const POLL_MS = 2500;

const readJson = (key, fallback) => {
  try { const raw = window.localStorage.getItem(key); return raw ? JSON.parse(raw) : fallback; } catch { return fallback; }
};
const writeJson = (key, value) => { try { window.localStorage.setItem(key, JSON.stringify(value)); } catch { /* storage unavailable */ } };

const clock = (iso) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

/** Server message -> widget bubble. Agent and system notices render as assistant-side bubbles with a label. */
function toBubble(m) {
  if (m.from === "visitor") return { id: m.id, from: "visitor", text: m.text, time: clock(m.createdAt) };
  if (m.from === "agent") return { id: m.id, from: "bot", text: m.text, time: clock(m.createdAt), label: `${m.agentName || "Agent"} · Support team` };
  if (m.from === "system") return { id: m.id, from: "bot", text: m.text, time: clock(m.createdAt), label: "Update" };
  return { id: m.id, from: "bot", text: m.text, time: clock(m.createdAt) };
}

/**
 * ChatWidgetContainer
 * ---------------------------------------------------------------------------
 * Top-level mount point for the visitor-facing widget. Everything runs through the API:
 *   - the bot (name, greeting, appearance) is loaded by id,
 *   - a conversation is created on the visitor's first message and survives refreshes,
 *   - bot replies, escalation and agent replies all arrive from the server (agent replies via polling),
 *   - lead capture is a real lead in Lead Management.
 */
export default function ChatWidgetContainer({ botName = "ChatBiz AI", startOpen = false, botId: botIdProp }) {
  const [bot, setBot] = useState(null);
  const [botMissing, setBotMissing] = useState(false);
  const [appearance, setAppearance] = useState(() => withDefaults());
  const { isOpen, unreadCount, toggle, close, notify, panelRef, launcherRef } = useChatWidgetState({ startOpen });

  const [conversationId, setConversationId] = useState(null);
  const [convo, setConvo] = useState(null); // latest server view of the conversation
  const [draft, setDraft] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [showLeadCapture, setShowLeadCapture] = useState(false);
  const [leadName, setLeadName] = useState("");
  const [leadEmail, setLeadEmail] = useState("");
  const [leadMessage, setLeadMessage] = useState("");
  const [leadExtra, setLeadExtra] = useState({ phone: "", company: "", message: "" });

  const seenCount = useRef(0);
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;
  const notifyRef = useRef(notify);
  notifyRef.current = notify;

  const messages = (convo ? convo.messages : [])
    .map(toBubble);
  const greeting = { id: "greeting", from: "bot", text: appearance.welcome || bot?.greeting || "Hi! How can I help you today?", time: "" };
  const shownMessages = messages.length ? messages : [greeting];

  // ---- load the bot and restore a previous conversation --------------------------------------------
  useEffect(() => {
    let active = true;
    getWidgetBot(botIdProp)
      .then((loaded) => {
        if (!active) return;
        setBot(loaded);
        setAppearance(withDefaults(loaded.appearance));
        const remembered = readJson(SESSION_KEY, {})[loaded.id];
        if (!remembered) return null;
        return getVisitorConversation(remembered)
          .then((existing) => {
            if (!active) return;
            if (existing.status === "Closed" || existing.status === "Resolved") return;
            setConversationId(remembered);
            setConvo(existing);
            seenCount.current = existing.messages.length;
          })
          .catch(() => {});
      })
      .catch(() => { if (active) setBotMissing(true); });
    return () => { active = false; };
  }, [botIdProp]);

  // ---- live appearance updates (Appearance & Branding saves dispatch this) ---------------------------
  useEffect(() => {
    const onAppearance = (event) => {
      const detail = event.detail || {};
      if (!bot || detail.botId === bot.id) setAppearance(withDefaults(detail.appearance));
    };
    window.addEventListener(APPEARANCE_EVENT, onAppearance);
    return () => window.removeEventListener(APPEARANCE_EVENT, onAppearance);
  }, [bot]);

  // ---- publish the session so the task panels can show it ------------------------------------------
  useEffect(() => {
    const context = {
      sessionId: conversationId || "Not started yet", channel: "Website",
      status: convo ? convo.status : "Idle", visitorName: convo?.visitorName || "", leadName: convo?.visitorName || "",
      leadEmail: convo?.visitorEmail || "", assignedAgent: convo?.assignedAgentName || "",
      history: (convo ? convo.messages : []).map((m) => ({ id: m.id, from: m.from === "visitor" ? "Visitor" : m.from === "agent" ? (m.agentName || "Agent") : "AI", text: m.text, time: clock(m.createdAt) })),
      source: "widget"
    };
    writeJson(CONTEXT_KEY, context);
    window.dispatchEvent(new CustomEvent("chatbizz_session_updated", { detail: context }));
  }, [conversationId, convo]);

  // A task panel (outside the widget) saved a name: prefill the widget's lead form.
  useEffect(() => {
    const onSession = (event) => {
      const detail = event.detail || {};
      if (detail.source === "page" && detail.visitorName) setLeadName((current) => current || detail.visitorName);
    };
    window.addEventListener("chatbizz_session_updated", onSession);
    return () => window.removeEventListener("chatbizz_session_updated", onSession);
  }, []);

  // ---- poll for agent replies / status changes ------------------------------------------------------
  useEffect(() => {
    if (!conversationId) return undefined;
    let active = true;
    const tick = () => {
      getVisitorConversation(conversationId)
        .then((latest) => {
          if (!active) return;
          const newOnes = latest.messages.slice(seenCount.current).filter((m) => m.from !== "visitor");
          if (latest.messages.length !== seenCount.current) {
            if (!isOpenRef.current && newOnes.length) notifyRef.current(newOnes.length);
            seenCount.current = latest.messages.length;
            setConvo(latest);
          } else if (latest.status !== undefined) {
            setConvo((current) => (current && current.status === latest.status && current.assignedAgentName === latest.assignedAgentName ? current : latest));
          }
        })
        .catch(() => {});
    };
    const timer = window.setInterval(tick, POLL_MS);
    return () => { active = false; window.clearInterval(timer); };
  }, [conversationId]);

  const rememberConversation = (id, botIdForSession) => {
    const map = readJson(SESSION_KEY, {});
    writeJson(SESSION_KEY, id ? { ...map, [botIdForSession]: id } : Object.fromEntries(Object.entries(map).filter(([k]) => k !== botIdForSession)));
  };

  const beginConversation = useCallback(async () => {
    const started = await startConversation(botIdProp ? { botId: botIdProp } : {});
    setConversationId(started.conversationId);
    setConvo(started.conversation);
    seenCount.current = started.conversation.messages.length;
    rememberConversation(started.conversationId, started.bot.id);
    return started;
  }, [botIdProp]);

  const sendConversationMessage = async (text) => {
    const cleanText = text.trim();
    if (!cleanText || isTyping) return;
    if (botMissing || !bot) {
      setDraft("");
      setConvo((current) => ({
        ...(current || { status: "Idle", messages: [] }),
        messages: [...(current ? current.messages : []),
          { id: `v-${Date.now()}`, from: "visitor", text: cleanText, createdAt: new Date().toISOString() },
          { id: `b-${Date.now()}`, from: "bot", text: "Chat isn't set up for this workspace yet.", createdAt: new Date().toISOString() }]
      }));
      return;
    }
    setDraft("");
    setIsTyping(true);
    // Optimistic bubble so the visitor sees their message immediately.
    setConvo((current) => ({
      ...(current || { status: "Open", messages: [] }),
      messages: [...(current ? current.messages : []), { id: `pending-${Date.now()}`, from: "visitor", text: cleanText, createdAt: new Date().toISOString() }]
    }));
    try {
      let id = conversationId;
      if (!id || convo?.status === "Closed" || convo?.status === "Resolved") id = (await beginConversation()).conversationId;
      let updated;
      try {
        updated = await sendVisitorMessage(id, cleanText);
      } catch (err) {
        if (err.code !== "CONVERSATION_CLOSED") throw err;
        id = (await beginConversation()).conversationId; // the agent closed it: start a fresh chat transparently
        updated = await sendVisitorMessage(id, cleanText);
      }
      seenCount.current = updated.messages.length;
      setConvo(updated);
    } catch (err) {
      setConvo((current) => ({
        ...(current || { status: "Open", messages: [] }),
        messages: [...(current ? current.messages.filter((m) => !String(m.id).startsWith("pending-")) : []),
          { id: `err-${Date.now()}`, from: "bot", text: "Sorry — I couldn't send that. Please try again.", createdAt: new Date().toISOString() }]
      }));
    } finally {
      setIsTyping(false);
    }
  };

  const handleSend = (text) => { sendConversationMessage(text); };
  const handleQuickAction = (qa) => { sendConversationMessage(qa.label); };

  const saveLead = async (event) => {
    event.preventDefault();
    const cleanName = leadName.trim();
    const cleanEmail = leadEmail.trim().toLowerCase();
    if (!cleanName) { setLeadMessage("Please enter your name."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) { setLeadMessage("Please enter a valid email address."); return; }
    const form = { ...DEFAULT_LEAD_FORM, ...(bot && bot.leadCapture) };
    const phone = form.phone === "off" ? "" : leadExtra.phone.trim();
    const company = form.company ? leadExtra.company.trim() : "";
    const note = form.message ? leadExtra.message.trim() : "";
    if (form.phone === "required" && !phone) { setLeadMessage("Please enter your phone number."); return; }
    const problem = phoneError(phone) || lengthError(company, { max: 80, label: "Company" }) || lengthError(note, { max: 500, label: "Message" });
    if (problem) { setLeadMessage(problem); return; }
    try {
      const id = conversationId || (await beginConversation()).conversationId;
      const result = await captureLead(id, { name: cleanName, email: cleanEmail, ...(phone ? { phone } : {}), ...(company ? { company } : {}), ...(note ? { message: note } : {}) });
      setConvo(result.conversation);
      setLeadMessage("Lead details saved successfully.");
      window.dispatchEvent(new CustomEvent("chatbizz_lead_captured", { detail: { name: cleanName, email: cleanEmail, source: "Website", duplicate: result.duplicate } }));
      seenCount.current = result.conversation.messages.length;
      setShowLeadCapture(false);
    } catch (err) {
      const f = err.details?.fields || {};
      setLeadMessage(f.name || f.email || f.phone || f.company || f.message || err.message || "Could not save your details.");
    }
  };

  return (
    <div className="cbai-root" style={{
      "--cbai-primary": appearance.primary,
      "--cbai-secondary": appearance.secondary,
      "--cbai-mobile-width": `${appearance.mobileWidth}px`,
      "--cbai-mobile-height": `${appearance.mobileHeight}px`,
      fontFamily: appearance.font,
      ...(appearance.position === "Bottom left"
        ? { left: `${appearance.desktopOffsetX}px`, right: "auto" }
        : { right: `${appearance.desktopOffsetX}px`, left: "auto" }),
      bottom: `${appearance.desktopOffsetY}px`,
    }}>
      <ChatWidget
        isOpen={isOpen}
        botName={appearance.showName ? (appearance.headerText || appearance.botName || botName) : "Your Assistant"}
        statusLabel={bot && bot.availability && !bot.availability.open ? "Team offline" : (appearance.headerStatus || "Online")}
        avatar={appearance.avatar}
        logo={appearance.logo}
        welcomeMessage={appearance.welcome}
        panelWidth={appearance.widgetWidth}
        panelHeight={appearance.widgetHeight}
        messages={shownMessages}
        quickActions={DEFAULT_QUICK_ACTIONS}
        draft={draft}
        onDraftChange={setDraft}
        onSend={handleSend}
        onQuickAction={handleQuickAction}
        isTyping={isTyping}
        onClose={close}
        panelRef={panelRef}
        showLeadCapture={showLeadCapture}
        onOpenLeadCapture={() => {
          setLeadMessage("");
          setLeadName((current) => current || convo?.visitorName || "");
          setLeadEmail((current) => current || convo?.visitorEmail || "");
          setShowLeadCapture(true);
        }}
        leadName={leadName}
        leadEmail={leadEmail}
        leadMessage={leadMessage}
        onLeadNameChange={setLeadName}
        onLeadEmailChange={setLeadEmail}
        onLeadSubmit={saveLead}
        onCloseLeadCapture={() => setShowLeadCapture(false)}
        leadConfig={{ ...DEFAULT_LEAD_FORM, ...(bot && bot.leadCapture) }}
        leadExtra={leadExtra}
        onLeadExtraChange={(field, value) => setLeadExtra((current) => ({ ...current, [field]: value }))}
      />
      <ChatWidgetLauncher
        isOpen={isOpen}
        unreadCount={unreadCount}
        onToggle={toggle}
        launcherRef={launcherRef}
        size={appearance.launcherSize}
        variant={appearance.launcher}
      />
    </div>
  );
}
